/**
 * Registration Page — Multi-Step
 * CCH Care - Version 1.0
 * 
 * Step 1: Organization Info
 * Step 2: Admin Account
 * Step 3: Facility Details
 * Step 4: Confirmation / Success
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register } from '../../api/auth';
import { COLORS } from '../../config/constants';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    organization_name: '',
    contact_email: '',
    contact_phone: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
    facility_name: '',
    facility_address_line1: '',
    facility_address_line2: '',
    facility_city: '',
    facility_state: '',
    facility_zip: '',
    bed_count: '',
  });

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  }

  function validateStep1() {
    if (!form.organization_name.trim()) return 'Organization name is required';
    if (!form.contact_email.trim()) return 'Contact email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) return 'Invalid email format';
    return null;
  }

  function validateStep2() {
    if (!form.admin_first_name.trim()) return 'First name is required';
    if (!form.admin_last_name.trim()) return 'Last name is required';
    if (!form.admin_email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_email)) return 'Invalid email format';
    if (!form.admin_password) return 'Password is required';
    if (form.admin_password.length < 12) return 'Password must be at least 12 characters';
    if (!/[A-Z]/.test(form.admin_password)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(form.admin_password)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(form.admin_password)) return 'Password must contain a number';
    if (!/[^A-Za-z0-9]/.test(form.admin_password)) return 'Password must contain a special character';
    if (form.admin_password !== form.admin_password_confirm) return 'Passwords do not match';
    return null;
  }

  function validateStep3() {
    if (!form.facility_name.trim()) return 'Facility name is required';
    if (!form.facility_address_line1.trim()) return 'Address is required';
    if (!form.facility_city.trim()) return 'City is required';
    if (!form.facility_state) return 'State is required';
    if (!form.facility_zip.trim()) return 'ZIP code is required';
    if (!form.bed_count || parseInt(form.bed_count) < 1) return 'Number of beds is required';
    return null;
  }

  function nextStep() {
    let err = null;
    if (step === 1) err = validateStep1();
    if (step === 2) err = validateStep2();
    if (step === 3) err = validateStep3();
    if (err) { setError(err); return; }
    setStep(step + 1);
  }

  async function handleSubmit() {
    const err = validateStep3();
    if (err) { setError(err); return; }

    setSubmitting(true);
    setError(null);

    try {
      const data = await register({
        ...form,
        bed_count: parseInt(form.bed_count),
      });
      setSuccess(data);
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  }

  // Shared styles
  const inputStyle = {
    width: '100%', padding: '14px 16px', fontSize: 15,
    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
    background: 'rgba(255,255,255,0.06)', color: '#fff',
    outline: 'none', fontFamily: 'inherit',
  };
  const labelStyle = {
    display: 'block', fontSize: 13, fontWeight: 600,
    color: '#93C5FD', marginBottom: 6,
  };
  const rowStyle = { marginBottom: 20 };
  const splitRow = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 };

  return (
    <div style={{
      minHeight: '100vh', background: '#0B1929', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        maxWidth: 540, width: '100%', background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: 40,
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: COLORS.primary,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
          }}>CCH Care</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>
            {step === 4 ? 'Registration Complete!' : 'Start Your Free Trial'}
          </div>
          {step < 4 && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
              {[1, 2, 3].map(s => (
                <div key={s} style={{
                  width: s === step ? 32 : 10, height: 10, borderRadius: 5,
                  background: s <= step ? COLORS.primary : 'rgba(255,255,255,0.1)',
                  transition: 'all 0.3s',
                }} />
              ))}
            </div>
          )}
          {step < 4 && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#64748B' }}>
              Step {step} of 3 — {step === 1 ? 'Organization' : step === 2 ? 'Admin Account' : 'Facility'}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 10, marginBottom: 20,
            background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.3)',
            color: '#FC8181', fontSize: 14, lineHeight: 1.5,
          }}>{error}</div>
        )}

        {/* Step 1: Organization */}
        {step === 1 && (
          <>
            <div style={rowStyle}>
              <label style={labelStyle}>Organization Name *</label>
              <input style={inputStyle} value={form.organization_name}
                onChange={e => update('organization_name', e.target.value)}
                placeholder="e.g. Sunrise Senior Care" autoFocus />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Contact Email *</label>
              <input style={inputStyle} type="email" value={form.contact_email}
                onChange={e => update('contact_email', e.target.value)}
                placeholder="billing@yourfacility.com" />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Contact Phone</label>
              <input style={inputStyle} type="tel" value={form.contact_phone}
                onChange={e => update('contact_phone', e.target.value)}
                placeholder="(555) 000-0000" />
            </div>
            <button onClick={nextStep} style={btnPrimary}>Continue →</button>
          </>
        )}

        {/* Step 2: Admin Account */}
        {step === 2 && (
          <>
            <div style={splitRow}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input style={inputStyle} value={form.admin_first_name}
                  onChange={e => update('admin_first_name', e.target.value)} autoFocus />
              </div>
              <div>
                <label style={labelStyle}>Last Name *</label>
                <input style={inputStyle} value={form.admin_last_name}
                  onChange={e => update('admin_last_name', e.target.value)} />
              </div>
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Admin Email *</label>
              <input style={inputStyle} type="email" value={form.admin_email}
                onChange={e => update('admin_email', e.target.value)}
                placeholder="your-name@yourfacility.com" />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Password *</label>
              <input style={inputStyle} type="password" value={form.admin_password}
                onChange={e => update('admin_password', e.target.value)}
                placeholder="Minimum 12 characters" />
              <div style={{ fontSize: 12, color: '#64748B', marginTop: 6, lineHeight: 1.6 }}>
                Must include: uppercase, lowercase, number, and special character
              </div>
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Confirm Password *</label>
              <input style={inputStyle} type="password" value={form.admin_password_confirm}
                onChange={e => update('admin_password_confirm', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(1)} style={btnSecondary}>← Back</button>
              <button onClick={nextStep} style={{ ...btnPrimary, flex: 2 }}>Continue →</button>
            </div>
          </>
        )}

        {/* Step 3: Facility */}
        {step === 3 && (
          <>
            <div style={rowStyle}>
              <label style={labelStyle}>Facility Name *</label>
              <input style={inputStyle} value={form.facility_name}
                onChange={e => update('facility_name', e.target.value)}
                placeholder="e.g. Sunrise Main Campus" autoFocus />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Address *</label>
              <input style={inputStyle} value={form.facility_address_line1}
                onChange={e => update('facility_address_line1', e.target.value)}
                placeholder="Street address" />
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Address Line 2</label>
              <input style={inputStyle} value={form.facility_address_line2}
                onChange={e => update('facility_address_line2', e.target.value)}
                placeholder="Suite, unit, floor (optional)" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>City *</label>
                <input style={inputStyle} value={form.facility_city}
                  onChange={e => update('facility_city', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>State *</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.facility_state}
                  onChange={e => update('facility_state', e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>ZIP *</label>
                <input style={inputStyle} value={form.facility_zip}
                  onChange={e => update('facility_zip', e.target.value)} maxLength={10} />
              </div>
            </div>
            <div style={rowStyle}>
              <label style={labelStyle}>Number of Beds / Residents *</label>
              <input style={inputStyle} type="number" min="1" max="9999" value={form.bed_count}
                onChange={e => update('bed_count', e.target.value)} inputMode="numeric" />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(2)} style={btnSecondary}>← Back</button>
              <button onClick={handleSubmit} disabled={submitting}
                style={{ ...btnPrimary, flex: 2, opacity: submitting ? 0.7 : 1 }}>
                {submitting ? 'Creating Account...' : 'Start Free Trial'}
              </button>
            </div>
            <div style={{ marginTop: 16, fontSize: 12, color: '#64748B', textAlign: 'center', lineHeight: 1.6 }}>
              By registering, you agree to our Terms of Service and Privacy Policy.
              Your 14-day trial includes full access to all features.
            </div>
          </>
        )}

        {/* Step 4: Success */}
        {step === 4 && success && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <p style={{ color: '#94A3B8', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
              Your organization <strong style={{ color: '#fff' }}>{form.organization_name}</strong> has been created 
              with a 14-day free trial. A welcome email has been sent to <strong style={{ color: '#fff' }}>{form.admin_email}</strong>.
            </p>
            <div style={{
              padding: 20, background: 'rgba(46, 204, 113, 0.1)', borderRadius: 12,
              border: '1px solid rgba(46, 204, 113, 0.2)', marginBottom: 24,
            }}>
              <div style={{ fontSize: 14, color: '#2ECC71', fontWeight: 600, marginBottom: 4 }}>Trial expires</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {new Date(success.trial_ends_at).toLocaleDateString('en-US', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                })}
              </div>
            </div>
            <button onClick={() => navigate('/login')} style={btnPrimary}>
              Sign In to Get Started →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Button styles
const btnPrimary = {
  width: '100%', padding: 16, border: 'none', borderRadius: 10,
  background: '#1B6B93', color: '#fff', fontSize: 16,
  fontWeight: 700, cursor: 'pointer', flex: 1,
};
const btnSecondary = {
  padding: 16, border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
  background: 'transparent', color: '#94A3B8', fontSize: 15,
  fontWeight: 600, cursor: 'pointer', flex: 1,
};
